import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../lib/api.js';

/**
 * useStaff — list fetch hook.
 *
 * Consumes:
 *   GET /api/dashboard/staff?status=active&search=foo
 *
 * Auto-refetches when `filters` change. Exposes manual refresh() for use
 * after mutations.
 */
export function useStaff(filters = {}) {
  const [state, setState] = useState({
    staff: [],
    loading: true,
    error: null,
  });

  // Stable key for dep-comparison. JSON.stringify is fine at the tiny
  // sizes we're working with and avoids over-refetching from object identity.
  const filterKey = JSON.stringify(filters);

  const load = useCallback(async () => {
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.search) params.set('search', filters.search);
      const qs = params.toString();
      const url = qs ? `/api/dashboard/staff?${qs}` : '/api/dashboard/staff';
      const data = await api.get(url);
      setState({ staff: data.staff || [], loading: false, error: null });
    } catch (e) {
      setState({ staff: [], loading: false, error: e });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  useEffect(() => {
    load();
  }, [load]);

  return { ...state, refresh: load };
}

/**
 * useStaffDetail — single-staff fetch, for the editor.
 */
export function useStaffDetail(id) {
  const [state, setState] = useState({
    staff: null,
    loading: !!id,
    error: null,
  });

  const load = useCallback(async () => {
    if (!id) {
      setState({ staff: null, loading: false, error: null });
      return;
    }
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const data = await api.get(`/api/dashboard/staff/${id}`);
      setState({ staff: data.staff, loading: false, error: null });
    } catch (e) {
      setState({ staff: null, loading: false, error: e });
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  return { ...state, refresh: load };
}

/**
 * Staff mutations — create, update, delete.
 *
 * Returns imperative methods rather than managing loading state. Each
 * caller (page/editor) manages its own submit-in-flight state to avoid
 * coupling the hook to UI concerns.
 */
export function useStaffMutations() {
  const createStaff = useCallback(async (payload) => {
    const data = await api.post('/api/dashboard/staff', payload);
    return data.staff;
  }, []);

  const updateStaff = useCallback(async (id, payload) => {
    const data = await api.put(`/api/dashboard/staff/${id}`, payload);
    return data.staff;
  }, []);

  const deleteStaff = useCallback(async (id) => {
    return api.del(`/api/dashboard/staff/${id}`);
  }, []);

  /**
   * Upload a photo for a staff member.
   * Uses multipart/form-data, not JSON — the api wrapper's auto-JSON
   * encoding is skipped by passing FormData directly as the body.
   */
  const uploadPhoto = useCallback(async (id, file) => {
    const form = new FormData();
    form.append('photo', file);
    const data = await api.post(`/api/dashboard/staff/${id}/photo`, form);
    return data; // { photo_url, photo_r2_key }
  }, []);

  const deletePhoto = useCallback(async (id) => {
    return api.del(`/api/dashboard/staff/${id}/photo`);
  }, []);

  return {
    createStaff,
    updateStaff,
    deleteStaff,
    uploadPhoto,
    deletePhoto,
  };
}

/**
 * Debounce wrapper. Used by the staff list search input to avoid
 * firing an API call on every keystroke.
 */
export function useDebouncedValue(value, delayMs = 300) {
  const [debounced, setDebounced] = useState(value);
  const timerRef = useRef(null);

  useEffect(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timerRef.current);
  }, [value, delayMs]);

  return debounced;
}
